import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { paymentStatus, formatUSD } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

export default function PaymentStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | failed | cancelled
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  const cancelled = location.pathname.includes("/cancel");

  useEffect(() => {
    if (cancelled) {
      setState("cancelled");
      return;
    }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      polls.current += 1;
      try {
        const data = await paymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("success");
          refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        // keep trying
      }
      if (polls.current >= 15) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [cancelled, location.search, refresh]);

  const content = {
    loading: { icon: <Loader2 size={40} className="animate-spin text-gold" />, title: "Confirming payment…", body: "Please hold while we verify your transaction securely." },
    success: {
      icon: <CheckCircle2 size={44} className="text-gold" />,
      title: info?.type === "topup" ? "Wallet topped up" : "Domain acquired",
      body: info?.type === "topup"
        ? `${formatUSD(info?.amount)} has been credited to your wallet.`
        : `${info?.domain_name || "Your domain"} is now yours. Congratulations.`,
    },
    failed: { icon: <XCircle size={44} className="text-red-400" />, title: "Payment not completed", body: "We couldn't confirm your payment. If you were charged, it will be reflected shortly." },
    cancelled: { icon: <XCircle size={44} className="text-ink-dim" />, title: "Checkout cancelled", body: "No charge was made. You can return to the collection anytime." },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-status">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-md w-full text-center bg-[var(--panel-soft)] border border-line rounded-2xl p-10 backdrop-blur-md"
      >
        <div className="w-20 h-20 mx-auto rounded-full border border-white/10 flex items-center justify-center mb-8">
          {content.icon}
        </div>
        <h1 className="font-serif text-4xl text-ink mb-3" data-testid="payment-status-title">{content.title}</h1>
        <p className="text-ink-dim text-sm leading-relaxed mb-8">{content.body}</p>
        {state !== "loading" && (
          <button
            onClick={() => navigate("/")}
            data-testid="payment-back-btn"
            className="rounded-full bg-gold text-bg font-sans font-bold text-sm px-8 py-3.5 hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow"
          >
            Back to Collection
          </button>
        )}
      </motion.div>
    </div>
  );
}
